// src/components/emailTemplate/EmailTemplatePreview.tsx
import React, { useState } from 'react';
import { 
  X, 
  Paperclip, 
  Download,
  Mail,
  User,
  Calendar,
  Eye,
  ChevronRight,
  FileText,
  Tag,
  Clock,
  UserCircle,
  Building2,
  Phone,
  MapPin,
  AtSign,
  Save
} from 'lucide-react';
import { EmailTemplate, getFileUrl } from '../../services/emailTemplate_service';
import '../styles/EmailTemplatePreview.css';

// Étendre l'interface EmailTemplate pour inclure createdAt et updatedAt
interface ExtendedEmailTemplate extends EmailTemplate {
  createdAt?: string;
  updatedAt?: string;
}

interface Props {
  template: ExtendedEmailTemplate;
  onClose: () => void;
}

export default function EmailTemplatePreview({ template, onClose }: Props) {
  const [testVariables, setTestVariables] = useState({
    prenom: 'Jean',
    nom: 'Dupont',
    email: 'jean.dupont@example.com',
    date: new Date().toLocaleDateString('fr-FR', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric'
    }),
    company: 'Ma Société',
    telephone: '01 23 45 67 89',
    adresse: '123 rue de Paris, 75001 Paris'
  });

  // Debug - Voir tout l'objet template
  console.log('Template:', template);
  console.log('createdAt:', template.createdAt);
  console.log('updatedAt:', template.updatedAt);

  // Fonction pour formater les dates ISO (2026-03-09T13:17:17.000Z)
  const formatISODate = (isoString: string | undefined | null) => {
    if (!isoString) return 'Date non disponible';
    
    try {
      const date = new Date(isoString);
      
      // Vérifier si la date est valide
      if (isNaN(date.getTime())) {
        return 'Date invalide';
      }
      
      // Format: DD/MM/YYYY HH:MM
      return date.toLocaleDateString('fr-FR', {
        day: '2-digit',
        month: '2-digit',
        year: 'numeric',
        hour: '2-digit',
        minute: '2-digit'
      });
      
    } catch (error) {
      console.error('Erreur formatage date:', error);
      return 'Date invalide';
    }
  };

  // Version sans heure
  const formatISODateSimple = (isoString: string | undefined | null) => {
    if (!isoString) return 'Date non disponible';
    
    try {
      const date = new Date(isoString);
      
      if (isNaN(date.getTime())) {
        return 'Date invalide';
      }
      
      return date.toLocaleDateString('fr-FR', {
        day: '2-digit',
        month: '2-digit',
        year: 'numeric'
      });
      
    } catch (error) {
      return 'Date non disponible';
    }
  };

  // Remplacer les variables par des valeurs de test
  const previewSubject = template.subject.replace(
    /{{(\w+)}}/g,
    (_, key) => testVariables[key as keyof typeof testVariables] || `{{${key}}}`
  );

  const previewContent = template.content.replace(
    /{{(\w+)}}/g,
    (_, key) => testVariables[key as keyof typeof testVariables] || `{{${key}}}`
  );

  const handleVariableChange = (key: string, value: string) => {
    setTestVariables(prev => ({ ...prev, [key]: value }));
  };

  const formatFileSize = (bytes: number): string => {
    if (!bytes) return '0 KB';
    if (bytes < 1024) return bytes + ' B';
    if (bytes < 1024 * 1024) return (bytes / 1024).toFixed(1) + ' KB';
    return (bytes / (1024 * 1024)).toFixed(1) + ' MB';
  };

  const resetVariables = () => {
    setTestVariables({
      prenom: 'Jean',
      nom: 'Dupont',
      email: 'jean.dupont@example.com',
      date: new Date().toLocaleDateString('fr-FR', {
        day: '2-digit',
        month: '2-digit',
        year: 'numeric'
      }),
      company: 'Ma Société',
      telephone: '01 23 45 67 89',
      adresse: '123 rue de Paris, 75001 Paris'
    });
  };

  const getCategoryLabel = (category: string) => {
    const categories: Record<string, { label: string; color: string }> = {
      welcome: { label: 'Bienvenue', color: '#10b981' },
      newsletter: { label: 'Newsletter', color: '#3b82f6' },
      notification: { label: 'Notification', color: '#8b5cf6' },
      marketing: { label: 'Marketing', color: '#f59e0b' },
      custom: { label: 'Personnalisé', color: '#6b7280' }
    };
    return categories[category] || { label: category, color: '#6b7280' };
  };

  const category = getCategoryLabel(template.category);

  return (
    <div className="preview-overlay" onClick={onClose}>
      <div className="preview-container" onClick={e => e.stopPropagation()}>
        {/* Header */}
        <div className="preview-header">
          <div className="preview-header-left">
            <div className="preview-header-icon">
              <Mail size={24} />
            </div>
            <div>
              <h2 className="preview-title">Aperçu du template</h2>
              <div className="preview-subtitle">
                <span className="preview-name">{template.name}</span>
                <span 
                  className="preview-category"
                  style={{ backgroundColor: `${category.color}15`, color: category.color }}
                >
                  {category.label}
                </span>
              </div>
            </div>
          </div>
          <button onClick={onClose} className="preview-close" title="Fermer">
            <X size={20} />
          </button>
        </div>

        <div className="preview-content">
          {/* Panneau latéral - Variables de test */}
          <div className="preview-sidebar">
            <div className="sidebar-section">
              <h3 className="sidebar-title">
                <Eye size={16} />
                Variables de test
              </h3>
              <p className="sidebar-description">
                Modifiez les valeurs pour voir le rendu en temps réel
              </p>
              
              <div className="variables-list">
                <div className="variable-item">
                  <label className="variable-label">
                    <UserCircle size={14} />
                    Prénom
                  </label>
                  <input
                    type="text"
                    value={testVariables.prenom}
                    onChange={(e) => handleVariableChange('prenom', e.target.value)}
                    className="variable-input"
                    placeholder="Prénom"
                  />
                </div>
                
                <div className="variable-item">
                  <label className="variable-label">
                    <User size={14} />
                    Nom
                  </label>
                  <input
                    type="text"
                    value={testVariables.nom}
                    onChange={(e) => handleVariableChange('nom', e.target.value)}
                    className="variable-input"
                    placeholder="Nom"
                  />
                </div>
                
                <div className="variable-item">
                  <label className="variable-label">
                    <AtSign size={14} />
                    Email
                  </label>
                  <input
                    type="email"
                    value={testVariables.email}
                    onChange={(e) => handleVariableChange('email', e.target.value)}
                    className="variable-input"
                    placeholder="Email"
                  />
                </div>
                
                <div className="variable-item">
                  <label className="variable-label">
                    <Phone size={14} />
                    Téléphone
                  </label>
                  <input
                    type="text"
                    value={testVariables.telephone}
                    onChange={(e) => handleVariableChange('telephone', e.target.value)}
                    className="variable-input"
                    placeholder="Téléphone"
                  />
                </div>
                
                <div className="variable-item">
                  <label className="variable-label">
                    <MapPin size={14} />
                    Adresse
                  </label>
                  <input
                    type="text"
                    value={testVariables.adresse}
                    onChange={(e) => handleVariableChange('adresse', e.target.value)}
                    className="variable-input"
                    placeholder="Adresse"
                  />
                </div>
                
                <div className="variable-item">
                  <label className="variable-label">
                    <Calendar size={14} />
                    Date
                  </label>
                  <input
                    type="text"
                    value={testVariables.date}
                    onChange={(e) => handleVariableChange('date', e.target.value)}
                    className="variable-input"
                    placeholder="Date"
                  />
                </div>
                
                <div className="variable-item">
                  <label className="variable-label">
                    <Building2 size={14} />
                    Société
                  </label>
                  <input
                    type="text"
                    value={testVariables.company}
                    onChange={(e) => handleVariableChange('company', e.target.value)}
                    className="variable-input"
                    placeholder="Société"
                  />
                </div>
              </div>

              <button onClick={resetVariables} className="reset-button">
                <Save size={14} />
                Réinitialiser
              </button>
            </div>

            {/* Informations du template */}
            <div className="sidebar-section">
              <h3 className="sidebar-title">
                <FileText size={16} />
                Informations
              </h3>
              
              <div className="info-list">
                {template.creator && (
                  <div className="info-item">
                    <User size={14} className="info-icon" />
                    <span className="info-label">Créé par :</span>
                    <span className="info-value">
                      {template.creator.name} {template.creator.firstname}
                    </span>
                  </div>
                )}
                
                <div className="info-item">
                  <Calendar size={14} className="info-icon" />
                  <span className="info-label">Créé le :</span>
                  <span className="info-value">
                    {formatISODateSimple(template.createdAt)}
                  </span>
                </div>

                <div className="info-item">
                  <Clock size={14} className="info-icon" />
                  <span className="info-label">Dernière modif :</span>
                  <span className="info-value">
                    {formatISODateSimple(template.updatedAt)}
                  </span>
                </div>
              </div>

              {template.description && (
                <div className="description-box">
                  <div className="description-title">Description</div>
                  <p className="description-text">{template.description}</p>
                </div>
              )}
            </div>
          </div>

          {/* Aperçu principal */}
          <div className="preview-main">
            {/* En-tête de l'aperçu */}
            <div className="preview-notice">
              <Eye size={16} />
              <span>Aperçu avec les variables de test (modifiables dans le panneau de gauche)</span>
              <ChevronRight size={16} className="notice-arrow" />
            </div>

            {/* Sujet */}
            <div className="subject-section">
              <div className="subject-label">Sujet de l'email</div>
              <div className="subject-value">
                {previewSubject}
              </div>
            </div>

            {/* Contenu */}
            <div className="content-section">
              <div className="content-label">Contenu</div>
              <div className="content-preview">
                <div 
                  dangerouslySetInnerHTML={{ __html: previewContent }} 
                  className="content-html"
                />
              </div>
            </div>

            {/* Pièces jointes */}
            {template.attachments && template.attachments.length > 0 && (
              <div className="attachments-section">
                <div className="attachments-header">
                  <Paperclip size={14} />
                  <span>Pièces jointes ({template.attachments.length})</span>
                </div>
                <div className="attachments-grid">
                  {template.attachments.map(att => {
                    const fileUrl = getFileUrl(att.file_path);
                    return (
                      <div key={att.id} className="attachment-card">
                        <div className="attachment-icon">
                          <Paperclip size={16} />
                        </div>
                        <div className="attachment-details">
                          <div className="attachment-name">{att.file_name}</div>
                          <div className="attachment-meta">
                            {formatFileSize(att.file_size)} • {att.file_type || 'Fichier'}
                          </div>
                        </div>
                        <a
                          href={fileUrl}
                          download={att.file_name}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="attachment-download"
                          title="Télécharger"
                        >
                          <Download size={14} />
                        </a>
                      </div>
                    );
                  })}
                </div>
              </div>
            )}
          </div>
        </div>

        {/* Footer */}
        <div className="preview-footer">
          <button onClick={onClose} className="btn-secondary">
            Fermer
          </button>
        </div>
      </div>
    </div>
  );
}